import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UtensilsCrossed, Loader2, AlertCircle, ShoppingCart,
  Plus, Minus, ClipboardList, Clock,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

/* ── is color "light"? → use dark text ── */
function isLight(hex = '') {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return false;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
}

function darkenHex(hex = '', amount = 55) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return hex;
  return '#' + [0, 2, 4].map(i =>
    Math.max(0, parseInt(h.substring(i, i + 2), 16) - amount)
      .toString(16).padStart(2, '0')
  ).join('');
}

export const CustomerMenu = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [property, setProperty] = useState(null);
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState({ items: [], totalAmount: 0 });
  const [itemQuantities, setItemQuantities] = useState({});
  const [addingItem, setAddingItem] = useState(null);
  const catBarRef = useRef(null);
  const menuTabsRef = useRef(null);

  /* ── initial fetch ── */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [allMenusRes, cartRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/device/all-menus/${deviceId}`),
          axios.get(`${BACKEND_URL}/api/cart/${deviceId}`),
        ]);

        if (allMenusRes.data.code !== 200) {
          setError(allMenusRes.data.message || 'No allocation found for this device');
          return;
        }

        const d = allMenusRes.data.data;
        setAllocation(d.allocation);
        setProperty(d.property);

        if (d.menus?.length > 0) {
          setMenus(d.menus);
          // default = first menu marked isDefault, else first
          const defaultMenu = d.menus.find(m => m.isDefault) || d.menus[0];
          setSelectedMenu(defaultMenu);

          // Fetch categories + tags
          const propertyId = d.property?.id || d.allocation?.propertyId;
          if (propertyId) {
            const [catRes, tagRes] = await Promise.all([
              axios.get(`${BACKEND_URL}/api/menu-categories/${propertyId}`),
              axios.get(`${BACKEND_URL}/api/menu-tags/${propertyId}`),
            ]);
            if (catRes.data.success) setCategories(catRes.data.categories || []);
            if (tagRes.data.success) setTags(tagRes.data.tags || []);
          }
        } else {
          setMenus([]);
        }

        if (cartRes.data.code === 200 && cartRes.data.data) {
          setCart(cartRes.data.data);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load menu. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId]);

  /* ── cart helpers ── */
  const getSelectQty = (id) => itemQuantities[id] || 1;
  const setSelectQty = (id, qty) => setItemQuantities(p => ({ ...p, [id]: Math.max(1, qty) }));
  const cartQtyOf = (id) => cart.items?.find(i => i.menuItemId === id)?.quantity || 0;
  const cartTotal = () => cart.items?.reduce((s, i) => s + i.quantity, 0) || 0;

  const addToCart = async (item) => {
    setAddingItem(item.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/cart/add`, {
        deviceId,
        menuItemId: item.id,
        menuItemName: item.name,
        quantity: getSelectQty(item.id),
        price: item.price || 0,
      });
      if (res.data.code === 200) {
        setCart(res.data.data);
        setItemQuantities(p => ({ ...p, [item.id]: 1 }));
      }
    } catch (_) {}
    setAddingItem(null);
  };

  /* ── theme ── */
  const themeColor = property?.themeColor || '#92400e';
  const darkColor = darkenHex(themeColor, 55);
  const textOnTheme = isLight(themeColor) ? '#1c1917' : '#ffffff';
  const mutedOnTheme = isLight(themeColor) ? 'rgba(28,25,23,0.65)' : 'rgba(255,255,255,0.65)';
  const logoUrl = property?.logoUrl || '';

  /* ── derived ── */
  const menuItems = selectedMenu ? (selectedMenu.items || []) : [];
  const filteredItems = selectedCategory === 'all'
    ? menuItems
    : menuItems.filter(i => i.categoryId === selectedCategory);

  const menuCategoryIds = [...new Set(menuItems.map(i => i.categoryId).filter(Boolean))];
  const menuCategories = categories
    .filter(c => menuCategoryIds.includes(c.id))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const getCategory = (id) => categories.find(c => c.id === id);
  const getTag = (id) => tags.find(t => t.id === id);

  /* ── switch menu ── */
  const switchMenu = (menu) => {
    setSelectedMenu(menu);
    setSelectedCategory('all');
  };

  /* ── loading ── */
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkenHex(themeColor, 55)} 100%)` }}>
        <div className="w-14 h-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        <p className="text-white/70 text-sm font-medium">Loading menu…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">Oops!</h2>
          <p className="text-stone-500 text-sm leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (!menus.length) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">No Menu Available</h2>
          <p className="text-stone-500 text-sm">There are no active menus for this property at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-32">

      {/* ── Full-width logo strip — no max-w constraint ── */}
      <div className="bg-white shadow-sm flex items-center justify-center px-6 py-3" style={{ minHeight: '56px' }}>
        {logoUrl ? (
          <img src={logoUrl} alt="Property logo" className="h-10 w-auto max-w-[180px] object-contain" data-testid="property-logo" />
        ) : (
          <span className="text-base font-bold text-stone-700 tracking-wide">{property?.name || 'SmartFlags'}</span>
        )}
      </div>

      {/* ── HERO HEADER (theme-coloured) ── */}
      <div
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkColor} 100%)` }}
      >
        {/* Decorative rings */}
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-white/5" />
        <div className="absolute -bottom-6 -left-6 w-36 h-36 rounded-full bg-white/5" />

        <div className="relative z-10 max-w-2xl mx-auto px-5 pt-6 pb-5">
          {/* Welcome */}
          {allocation?.guestName ? (
            <div className="mb-4">
              <p className="text-xs uppercase tracking-widest font-semibold mb-0.5" style={{ color: mutedOnTheme }}>Welcome</p>
              <h1 className="text-2xl font-bold leading-tight" style={{ color: textOnTheme }}>
                {allocation.guestName}
              </h1>
              <p className="text-sm mt-1 flex items-center gap-1.5" style={{ color: mutedOnTheme }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: textOnTheme, opacity: 0.6 }} />
                Room {allocation.roomNumber}
                {allocation.numAdults > 0 && (
                  <span className="ml-1.5" style={{ color: mutedOnTheme, opacity: 0.7 }}>
                    · {allocation.numAdults} guest{allocation.numAdults > 1 ? 's' : ''}
                    {allocation.numKids > 0 ? ` + ${allocation.numKids} child${allocation.numKids > 1 ? 'ren' : ''}` : ''}
                  </span>
                )}
              </p>
            </div>
          ) : (
            <h1 className="text-2xl font-bold mb-4" style={{ color: textOnTheme }}>Our Menu</h1>
          )}

          {/* ── MENU TABS (all menus) ── */}
          <div
            ref={menuTabsRef}
            className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide items-center"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {menus.map((m) => {
              const isSelected = selectedMenu?.id === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => switchMenu(m)}
                  className="relative whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-all shrink-0 flex items-center gap-1.5"
                  style={{
                    background: isSelected
                      ? isLight(themeColor) ? 'rgba(0,0,0,0.75)' : '#ffffff'
                      : isLight(themeColor) ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.15)',
                    color: isSelected
                      ? isLight(themeColor) ? '#ffffff' : themeColor
                      : textOnTheme,
                  }}
                  data-testid={`menu-tab-${m.id}`}
                >
                  <UtensilsCrossed className="w-3 h-3 shrink-0" />
                  {m.name}
                  {m.isDefault && !isSelected && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title="Your section's menu" />
                  )}
                </button>
              );
            })}

            {/* My Orders shortcut */}
            <button
              onClick={() => navigate(`/customer/orders/${deviceId}`)}
              className="whitespace-nowrap ml-auto text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 shrink-0 transition-all"
              style={{ background: isLight(themeColor) ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.15)', color: textOnTheme }}
              data-testid="view-my-orders-btn"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              My Orders
            </button>
          </div>
        </div>

        {/* Wave bottom */}
        <svg viewBox="0 0 1440 30" className="w-full -mb-px" preserveAspectRatio="none" style={{ height: '30px', fill: '#f8f7f6' }}>
          <path d="M0,15 C360,30 1080,0 1440,15 L1440,30 L0,30 Z" />
        </svg>
      </div>

      {/* ── STICKY CATEGORY PILLS ── */}
      {menuCategories.length > 0 && (
        <div className="sticky top-0 z-20 bg-stone-50/95 backdrop-blur-sm shadow-sm">
          <div ref={catBarRef} className="max-w-2xl mx-auto flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-full transition-all ${
                selectedCategory === 'all' ? 'text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200'
              }`}
              style={selectedCategory === 'all' ? { backgroundColor: themeColor } : {}}
              data-testid="cat-filter-all"
            >
              All
            </button>
            {menuCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-full transition-all ${
                  selectedCategory === cat.id ? 'text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200'
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: themeColor } : {}}
                data-testid={`cat-filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── ITEMS GRID ── */}
      <div className="max-w-2xl mx-auto px-3 pt-4">
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center mt-4">
            <UtensilsCrossed className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-medium">No items in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredItems.map((item) => {
              const category = getCategory(item.categoryId);
              const inCartQty = cartQtyOf(item.id);
              const selectQty = getSelectQty(item.id);
              const isUnavailable = item.isAvailable === false;
              const lowStock = item.portionsLeft != null && item.portionsLeft <= 5;
              const itemTags = (item.tagIds || []).map(getTag).filter(Boolean);

              return (
                <div
                  key={item.id}
                  className={`bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col transition-all ${isUnavailable ? 'opacity-55' : 'active:scale-[0.98]'}`}
                  data-testid={`menu-item-${item.id}`}
                >
                  {/* Image */}
                  <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UtensilsCrossed className="w-10 h-10 text-stone-300" />
                      </div>
                    )}
                    {category && (
                      <span className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm text-white text-[9px] font-semibold px-2 py-0.5 rounded-full leading-4">
                        {category.name}
                      </span>
                    )}
                    {inCartQty > 0 && (
                      <span className="absolute top-2 right-2 w-5 h-5 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center shadow"
                        style={{ backgroundColor: themeColor }}>
                        {inCartQty}
                      </span>
                    )}
                    {lowStock && !isUnavailable && (
                      <span className="absolute bottom-2 left-2 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        {item.portionsLeft} left
                      </span>
                    )}
                    {isUnavailable && (
                      <div className="absolute inset-0 bg-stone-900/40 flex items-center justify-center">
                        <span className="bg-white/90 text-stone-700 text-[10px] font-bold px-2.5 py-1 rounded-full shadow">Unavailable</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 p-3 gap-2">
                    {itemTags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {itemTags.slice(0, 2).map((tag) => (
                          <span key={tag.id} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-4"
                            style={{ backgroundColor: `${tag.color}25`, color: tag.color }}>
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3 className="text-[13px] font-bold text-stone-800 leading-snug line-clamp-2">{item.name}</h3>
                    {item.description && (
                      <p className="text-[11px] text-stone-400 leading-relaxed line-clamp-2 -mt-1">{item.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-1">
                      <span className="text-[15px] font-extrabold leading-none" style={{ color: themeColor }}>{fmt(item.price)}</span>
                      {item.prepTime > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-stone-400">
                          <Clock className="w-3 h-3 shrink-0" />{item.prepTime}m
                        </span>
                      )}
                    </div>
                    {!isUnavailable && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="flex items-center bg-stone-100 rounded-lg overflow-hidden shrink-0">
                          <button onClick={() => setSelectQty(item.id, selectQty - 1)}
                            className="w-6 h-7 flex items-center justify-center text-stone-500 hover:bg-stone-200 transition-colors"
                            data-testid={`qty-minus-${item.id}`}>
                            <Minus className="w-2.5 h-2.5" />
                          </button>
                          <span className="w-5 text-center text-[12px] font-bold text-stone-700 select-none">{selectQty}</span>
                          <button onClick={() => setSelectQty(item.id, selectQty + 1)}
                            className="w-6 h-7 flex items-center justify-center text-stone-500 hover:bg-stone-200 transition-colors"
                            data-testid={`qty-plus-${item.id}`}>
                            <Plus className="w-2.5 h-2.5" />
                          </button>
                        </div>
                        <button
                          onClick={() => addToCart(item)}
                          disabled={addingItem === item.id}
                          className="flex-1 h-7 flex items-center justify-center rounded-lg text-[11px] font-bold transition-all disabled:opacity-50 text-white"
                          style={{ backgroundColor: inCartQty > 0 ? '#10b981' : themeColor }}
                          data-testid={`add-to-cart-${item.id}`}
                        >
                          {addingItem === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── FLOATING CART BUTTON ── */}
      {cartTotal() > 0 && (
        <div className="fixed bottom-5 left-4 right-4 max-w-2xl mx-auto z-30">
          <button
            onClick={() => navigate(`/customer/cart/${deviceId}`)}
            className="w-full rounded-2xl py-4 px-5 shadow-xl flex items-center justify-between transition-all active:scale-[0.98] text-white"
            style={{ backgroundColor: themeColor, boxShadow: `0 8px 24px ${themeColor}55` }}
            data-testid="view-cart-btn"
          >
            <div className="flex items-center gap-3">
              <div className="relative w-7 h-7">
                <ShoppingCart className="w-7 h-7" />
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-white rounded-full text-[10px] font-extrabold flex items-center justify-center shadow"
                  style={{ color: themeColor }}>
                  {cartTotal()}
                </span>
              </div>
              <div className="text-left">
                <p className="text-xs opacity-80 leading-none mb-0.5">View your order</p>
                <p className="font-bold text-base leading-none">{cartTotal()} item{cartTotal() > 1 ? 's' : ''}</p>
              </div>
            </div>
            <span className="font-extrabold text-lg">{fmt(cart.totalAmount)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerMenu;
